import React, { useEffect, useState } from "react";

import { trackPage, trackService, displayUrl } from "../utils/track";

/**
 * A live feed on the left of the screen showing where recent visitors came
 * from and which service they opened.
 *
 * Registers this visit on mount, then polls for the latest few. Only a city,
 * country and service name are ever shown — there is no identifier behind a
 * row, so nothing can be traced to a person or two rows tied together.
 *
 * Renders nothing when the service is unreachable or has nothing to show.
 * An empty panel would be worse than no panel.
 */

// Read when the effect runs rather than at import — see utils/track.
const telemetryUrl = () => (process.env.REACT_APP_TELEMETRY_URL || "").replace(/\/$/, "");
const HISTORY = 7;
const POLL_MS = 20_000;

/** Two-letter country code to the matching flag emoji. */
function flagFor(code) {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return "";
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

/** "3m ago" — a feed reads better in relative time. */
function timeAgo(iso, now = Date.now()) {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function VisitorLocation() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const base = telemetryUrl();
    if (!base) return undefined;
    const controller = new AbortController();

    // One call does both jobs: it registers this visit and returns the feed.
    // `refresh=1` on later polls skips the registration, so a tab left open
    // does not count as a new visitor every twenty seconds.
    const load = (refresh) =>
      fetch(
        `${base}/api/visitor?limit=${HISTORY}` +
          (refresh ? "&refresh=1" : `&page=${encodeURIComponent(window.location.pathname)}`),
        { signal: controller.signal }
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && Array.isArray(data.recent)) setRows(data.recent);
        })
        .catch(() => {
          // Telemetry is an observer. If it is down, the page carries on.
        });

    load(false);
    const timer = setInterval(() => load(true), POLL_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, []);

  if (!rows.length) return null;

  return (
    <aside className={`visitor-feed${open ? "" : " collapsed"}`} aria-label="Recent visitors">
      <button
        type="button"
        className="visitor-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="visitor-live" aria-hidden="true" />
        <span className="mono">Live · recent visitors</span>
        <span className="visitor-toggle" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>

      {open && (
        <ul className="visitor-list">
          {rows.map((r) => (
            <li key={`${r.at}-${r.city}-${r.service}`}>
              <span className="visitor-flag" aria-hidden="true">
                {flagFor(r.countryCode)}
              </span>
              <span className="visitor-where">
                <strong>{r.city ? `${r.city}, ${r.countryCode}` : r.country}</strong>
                <span className="visitor-service" title={displayUrl(r.page)}>
                  {displayUrl(r.page)}
                </span>
              </span>
              <span className="visitor-when mono">{timeAgo(r.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

export { trackPage, trackService };
export default VisitorLocation;
